import { Injectable } from '@angular/core';
import {
  FrontendApiUpdateSettingsFlowRequest,
  SettingsFlow,
} from '@ory/client';
import { Observable } from 'rxjs';
import { OryCreateInput, OryExecuteInput, OryExecuteOutput, OryGetOutput } from '../../model';
import type { OryGetInput } from '../../model';
import { OryKratosBaseFlow } from './base.flow';
import { OryFlowReader } from '../../utils';
import type {
  OryCreatableFlow,
  OryRetrievableFlow,
  OryExecutableFlow,
} from '../flow-capabilities';

/**
 * Implements Ory Kratos settings flow for updating user profile and security settings.
 *
 * Allows authenticated users to modify:
 * - Identity traits (email, name, etc.)
 * - Password
 * - OIDC connections (link/unlink social accounts)
 * - WebAuthn credentials
 *
 * Requires active session. May require reauthentication for sensitive changes.
 */
@Injectable({
  providedIn: 'root'
})
export class OrySettingsFlow<IdentityTraits extends object = object>
  extends OryKratosBaseFlow
  implements
    OryCreatableFlow<'settings', IdentityTraits>,
    OryRetrievableFlow<'settings', IdentityTraits>,
    OryExecutableFlow<'settings', IdentityTraits>
{
  create(
    input: OryCreateInput<'settings'> = {},
  ): Observable<OryExecuteOutput<'settings', IdentityTraits>> {
    return this.fromOryRequest(this.iamApi.createBrowserSettingsFlow(input));
  }

  get(input: OryGetInput<'settings'>): Observable<SettingsFlow> {
    return this.fromOryRequest(this.iamApi.getSettingsFlow(input));
  }

  execute(
    flow: OryGetOutput<'settings', IdentityTraits>,
    input: OryExecuteInput<'settings'>,
  ): Observable<OryExecuteOutput<'settings', IdentityTraits>> {
    const csrfToken = OryFlowReader.csrfToken(flow);
    const apiRequest: FrontendApiUpdateSettingsFlowRequest = {
      flow: flow.id,
      updateSettingsFlowBody: input.method === 'oidc' ? { ...input } : { ...input, csrf_token: csrfToken }
    };

    return this.fromOryRequest(this.iamApi.updateSettingsFlow(apiRequest));
  }

}
